import { Fragment, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2, ShieldCheck } from 'lucide-react';
import { OrgRolesApi, RoleView } from '@/services/org-roles.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Org tier — Role & Permission, cross-school. Mirrors the school-tier RolesController's catalog (modules x actions) in a checkbox matrix — there's no frontend for this anywhere yet, school-tier or org-tier, so this is the first UI built for it. */
export function OrgRolesPage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [modal, setModal] = useState<{ open: boolean; role?: RoleView }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; role?: RoleView }>({ open: false });

  const { data: schools = [] } = useQuery({ queryKey: ['org-roles-schools'], queryFn: () => OrgRolesApi.schools() });
  const { data: catalog } = useQuery({ queryKey: ['org-roles-catalog'], queryFn: () => OrgRolesApi.catalog() });

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['org-roles', schoolId],
    queryFn: () => OrgRolesApi.list(schoolId),
    enabled: !!schoolId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-roles', schoolId] });

  const save = useMutation({
    mutationFn: (v: { id?: string; data: { name: string; description?: string; permissions: string[] } }) =>
      v.id ? OrgRolesApi.update(v.id, schoolId, v.data) : OrgRolesApi.create(schoolId, v.data),
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => OrgRolesApi.remove(id, schoolId),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader title="Role & Permission" description="Custom roles and their permission sets, for a school in your organization. Built-in roles (Owner, Admin, …) are shown read-only." />

      <div className="mb-4 flex items-center gap-2">
        <Select className="!w-64" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <div className="flex-1" />
        <button className="btn-primary" onClick={() => setModal({ open: true })} disabled={!schoolId || !catalog}>
          <Plus className="mr-1.5 h-4 w-4" /> Create role
        </button>
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to manage its roles.</div>
      ) : isLoading ? (
        <div className="card p-8 text-center text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="space-y-2">
          {roles.map((r) => (
            <div key={r.key} className="card flex items-center justify-between gap-3 p-4">
              <div>
                <div className="flex items-center gap-2 font-medium text-slate-900">
                  <ShieldCheck className="h-4 w-4 text-slate-400" />
                  {r.name}
                  {r.isSystem && (
                    <Badge tone="slate" className="ml-1">
                      Built-in
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {r.description ?? 'No description'} · {r.permissions.length} permission{r.permissions.length === 1 ? '' : 's'} · {r.userCount} user
                  {r.userCount === 1 ? '' : 's'}
                </div>
              </div>
              {!r.isSystem && r.id && (
                <div className="flex shrink-0 items-center gap-1">
                  <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600" onClick={() => setModal({ open: true, role: r })} title="Edit">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirm({ open: true, role: r })} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {catalog && (
        <RoleFormModal
          open={modal.open}
          role={modal.role}
          catalog={catalog}
          saving={save.isPending}
          errorMsg={errMsg(save.error)}
          onClose={() => setModal({ open: false })}
          onSubmit={(data) => save.mutate({ id: modal.role?.id ?? undefined, data })}
        />
      )}

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.role?.id && remove.mutate(confirm.role.id)}
        loading={remove.isPending}
        title="Delete role?"
        message={`Remove the "${confirm.role?.name}" role. Fails if any user still has it — reassign them first.`}
        confirmText="Delete"
      />
    </>
  );
}

function RoleFormModal({
  open,
  role,
  catalog,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  role?: RoleView;
  catalog: { modules: { key: string; label: string; group: string; actions: string[] }[] };
  onClose: () => void;
  onSubmit: (v: { name: string; description?: string; permissions: string[] }) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string; description: string }>({
    values: { name: role?.name ?? '', description: role?.description ?? '' },
  });
  const [permissions, setPermissions] = useState<Set<string>>(new Set(role?.permissions ?? []));

  useEffect(() => {
    if (open) setPermissions(new Set(role?.permissions ?? []));
  }, [open, role]);

  const grouped = useMemo(() => {
    const byGroup = new Map<string, typeof catalog.modules>();
    for (const m of catalog.modules) {
      const list = byGroup.get(m.group) ?? [];
      list.push(m);
      byGroup.set(m.group, list);
    }
    return [...byGroup.entries()];
  }, [catalog]);

  function toggle(perm: string, checked: boolean) {
    setPermissions((prev) => {
      const next = new Set(prev);
      if (checked) next.add(perm);
      else next.delete(perm);
      return next;
    });
  }

  function handleClose() {
    reset();
    setPermissions(new Set(role?.permissions ?? []));
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={role ? `Edit ${role.name}` : 'Create role'}
      size="xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={saving}
            onClick={handleSubmit((v) => onSubmit({ name: v.name, description: v.description || undefined, permissions: [...permissions] }))}
          >
            {saving ? 'Saving…' : role ? 'Save changes' : 'Create role'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Role name" required error={errors.name?.message}>
            <Input {...register('name', { required: 'Required' })} />
          </Field>
          <Field label="Description">
            <Input {...register('description')} />
          </Field>
        </div>

        <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Module</th>
                <th className="px-3 py-2 text-center font-medium text-slate-600">List</th>
                <th className="px-3 py-2 text-center font-medium text-slate-600">Create</th>
                <th className="px-3 py-2 text-center font-medium text-slate-600">Delete</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {grouped.map(([group, modules]) => (
                <Fragment key={group}>
                  <tr className="bg-slate-50/60">
                    <td colSpan={4} className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {group}
                    </td>
                  </tr>
                  {modules.map((m) => (
                    <tr key={m.key}>
                      <td className="px-3 py-1.5 text-slate-700">{m.label}</td>
                      {(['list', 'create', 'delete'] as const).map((action) => {
                        const perm = `${m.key}:${action}`;
                        return (
                          <td key={action} className="px-3 py-1.5 text-center">
                            {m.actions.includes(action) ? (
                              <input type="checkbox" checked={permissions.has(perm)} onChange={(e) => toggle(perm, e.target.checked)} />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {errorMsg && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </div>
    </Modal>
  );
}
